
import { useCallback, useRef } from 'react';
import { Attendee } from '@/types/attendee';
import { AdminAction } from '@/types/adminTypes';
import { useAttendanceScanner } from '@/hooks/admin/useAttendanceScanner';
import { useAttendanceConfirmation } from '@/hooks/admin/useAttendanceConfirmation';
import { updateAttendance } from '@/lib/supabase/client';

type ToastFunction = {
  toast: (props: { title: string; description: string; variant?: "default" | "destructive" }) => void;
};

export const useAdminAttendance = (
  attendees: Attendee[],
  recordAction: (actionType: AdminAction['actionType'], adminName: string, details?: string, attendeeCode?: string) => void,
  adminName: string,
  { toast }: ToastFunction,
  refreshAttendees: () => Promise<Attendee[]>
) => {
  // Keep track of whether attendees need refreshing
  const needsRefresh = useRef<boolean>(true);
  const lastScannedCode = useRef<string | null>(null);
  const lastUpdateTimes = useRef<Record<string, number>>({});
  
  // Direct database update function (critical path)
  const directDatabaseUpdate = useCallback(async (code: string, attended: boolean) => {
    if (!code) return false;
    
    // Throttle updates to avoid database overload (limit to once per 3 seconds per code)
    const now = Date.now();
    const lastTime = lastUpdateTimes.current[code] || 0;
    if (now - lastTime < 3000) {
      console.log(`Throttling update for ${code} - too recent`);
      return false;
    }
    
    try {
      console.log(`Direct database update for ${code}, attended=${attended}`);
      lastUpdateTimes.current[code] = now;
      
      // Get timestamp
      const timestamp = attended ? new Date().toISOString() : null;
      
      // Call database update function directly
      const result = await updateAttendance(
        code,
        attended,
        timestamp
      );
      
      if (result.success && result.data) {
        console.log(`Direct update successful for ${code}`);
        
        // Create and dispatch custom event with useModalOnly flag
        const event = new CustomEvent('database-direct-update', {
          detail: { 
            attendee: result.data,
            timestamp: timestamp || null,
            success: true,
            useModalOnly: true
          }
        });
        window.dispatchEvent(event);
        
        // Request refresh
        needsRefresh.current = true;
        try {
          await refreshAttendees();
        } catch (error) {
          console.error('Error refreshing attendees after direct update:', error);
        }
        
        return true;
      }
      
      console.error('Direct database update failed:', result.error);
      return false;
    } catch (error) {
      console.error('Error during direct database update:', error);
      return false;
    }
  }, [refreshAttendees]);
  
  // Use confirmation hook with enhanced capability
  const {
    selectedAttendee,
    isDialogOpen,
    setIsDialogOpen,
    isConfirming,
    handleAttendeeFound,
    confirmAttendance: originalConfirmAttendance,
    directConfirmAttendance,
    setSelectedAttendee
  } = useAttendanceConfirmation(recordAction, adminName, { toast });
  
  // Enhanced attendee found handler that can handle direct check-in for already checked-in attendees
  const enhancedHandleAttendeeFound = useCallback((attendee: Attendee) => {
    // Save the code being processed
    lastScannedCode.current = attendee.code;
    
    console.log(`Attendee found handler called with: ${attendee.name} (${attendee.code})`);
    
    if (attendee.attended) {
      // For already checked-in attendees, show a toast instead of opening dialog
      toast({
        title: "!!! Đã được điểm danh !!!",
        description: `${attendee.name} đã được điểm danh trước đó !!!`,
        variant: "destructive"
      });
      // Do not show dialog for already checked-in attendees
      return;
    }
    
    // If attendee has paid, try direct check-in first
    if (attendee.paid) {
      // Try direct confirmation for paid attendees to increase success rate
      directConfirmAttendance(attendee).then(success => {
        if (!success) {
          // If direct update fails, use normal flow
          handleAttendeeFound(attendee);
        }
      });
    } else {
      // Normal flow for attendees not checked in yet or not paid
      handleAttendeeFound(attendee);
    }
  }, [handleAttendeeFound, toast, directConfirmAttendance]);
  
  // Use scanner hook with enhanced direct update capability
  const {
    handleQrScan,
    handleCodeSubmit,
    simulateQrScan
  } = useAttendanceScanner(
    attendees, 
    recordAction, 
    adminName, 
    enhancedHandleAttendeeFound, 
    { toast },
    refreshAttendees // Pass refresh function to get most current data
  );
  
  // Wrap confirmAttendance to ensure refresh happens
  const confirmAttendance = useCallback(async () => {
    console.log("Starting attendance confirmation process");
    const result = await originalConfirmAttendance();
    
    if (result) {
      console.log("Attendance confirmed successfully, marking refresh flag");
      // Mark that we need to refresh the data
      needsRefresh.current = true;
      
      // Force immediate refresh of attendee data
      console.log("Forcing immediate refresh of attendee data");
      try {
        await refreshAttendees();
        console.log("Completed refreshing attendee data after confirmation");
      } catch (error) {
        console.error("Error refreshing attendees after confirmation:", error);
      }
    }
    return result;
  }, [originalConfirmAttendance, refreshAttendees]);
  
  return {
    selectedAttendee,
    isDialogOpen,
    setIsDialogOpen,
    isConfirming,
    handleQrScan,
    handleCodeSubmit,
    confirmAttendance,
    simulateQrScan,
    setSelectedAttendee,
    directDatabaseUpdate
  };
};
